import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaArrowLeft,
    FaSearch,
    FaSyncAlt,
    FaMicrochip,
    FaEdit,
    FaSave,
    FaTimes,
    FaUser,
    FaCalendarAlt
} from 'react-icons/fa';

import { request } from '../../services/api';
import { CircuitBackgroundLayer } from '../../components/CircuitBackground/AnimatedCircuitChipBackground';
import styles from '../Monitor/Monitor.module.css';

const AdminDevices = () => {
    const navigate = useNavigate();
    const ownerDropdownRef = useRef(null);

    const [search, setSearch] = useState('');
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [editDevice, setEditDevice] = useState(null);
    const [editForm, setEditForm] = useState({
        device_serial: '',
        device_name: '',
        owner_id: '',
        owner_label: '',
        start_date: '',
        due_date: ''
    });
    const [editLoading, setEditLoading] = useState(false);

    const [ownerSearch, setOwnerSearch] = useState('');
    const [ownerUsers, setOwnerUsers] = useState([]);
    const [ownerUsersLoading, setOwnerUsersLoading] = useState(false);
    const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false);

    const loadDevices = async (searchText = search) => {
        setLoading(true);
        setError('');

        try {
            const json = await request(
                `/admin_get_devices.php?search=${encodeURIComponent(searchText.trim())}&limit=100`
            );

            if (!json || json.status !== 'success') {
                throw new Error(json?.message || 'خطا در دریافت دستگاه‌ها');
            }

            setDevices(json.devices || []);
        } catch (err) {
            console.error('Admin devices load error:', err);
            setError(err.message || 'خطا در دریافت دستگاه‌ها');
            setDevices([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            loadDevices(search);
        }, 350);

        return () => clearTimeout(timer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const searchOwners = async (text = ownerSearch) => {
        setOwnerUsersLoading(true);

        try {
            const json = await request(
                `/admin_get_users.php?search=${encodeURIComponent(text.trim())}&limit=50`
            );

            if (!json || json.status !== 'success') {
                throw new Error(json?.message || 'خطا در دریافت کاربران');
            }

            setOwnerUsers(json.users || []);
        } catch (err) {
            console.error('Search owners error:', err);
            setOwnerUsers([]);
            setError(err.message || 'خطا در دریافت کاربران');
        } finally {
            setOwnerUsersLoading(false);
        }
    };

    useEffect(() => {
        if (!editDevice) return;
        if (!isOwnerDropdownOpen) return;

        const timer = setTimeout(() => {
            searchOwners(ownerSearch);
        }, 350);

        return () => clearTimeout(timer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownerSearch, isOwnerDropdownOpen, editDevice]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                ownerDropdownRef.current &&
                !ownerDropdownRef.current.contains(event.target)
            ) {
                setIsOwnerDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const openEditModal = (device) => {
        const ownerLabel = buildOwnerLabel(device);

        setEditDevice(device);
        setEditForm({
            device_serial: device.device_serial || '',
            device_name: device.device_name || '',
            owner_id: device.owner_id || '',
            owner_label: ownerLabel,
            start_date: device.start_date || '',
            due_date: device.due_date || ''
        });

        setOwnerSearch(ownerLabel);
        setOwnerUsers([]);
        setIsOwnerDropdownOpen(false);
        setError('');
    };

    const closeEditModal = () => {
        setEditDevice(null);
        setEditForm({
            device_serial: '',
            device_name: '',
            owner_id: '',
            owner_label: '',
            start_date: '',
            due_date: ''
        });

        setOwnerSearch('');
        setOwnerUsers([]);
        setIsOwnerDropdownOpen(false);
        setEditLoading(false);
    };

    const submitEditDevice = async (e) => {
        e.preventDefault();

        if (!editDevice) return;

        if (!editForm.device_serial.trim()) {
            setError('سریال دستگاه الزامی است');
            return;
        }

        if (!editForm.owner_id) {
            setError('مالک دستگاه الزامی است');
            return;
        }

        setEditLoading(true);
        setError('');

        try {
            const json = await request('/admin_update_device.php', 'POST', {
                device_id: editDevice.id,
                device_serial: editForm.device_serial.trim(),
                device_name: editForm.device_name.trim(),
                owner_id: Number(editForm.owner_id),
                start_date: editForm.start_date || '',
                due_date: editForm.due_date || ''
            });

            if (!json || json.status !== 'success') {
                throw new Error(json?.message || 'خطا در ویرایش دستگاه');
            }

            setDevices(prev =>
                prev.map(item =>
                    item.id === editDevice.id
                        ? { ...item, ...json.device }
                        : item
                )
            );

            closeEditModal();
        } catch (err) {
            console.error('Update device error:', err);
            setError(err.message || 'خطا در ویرایش دستگاه');
        } finally {
            setEditLoading(false);
        }
    };

    const handleOwnerInputFocus = () => {
        setIsOwnerDropdownOpen(true);

        if (ownerUsers.length === 0) {
            searchOwners(ownerSearch);
        }
    };

    const handleSelectOwner = (user) => {
        const label = `${user.name || ''} ${user.family || ''} - ${user.phone_num1 || user.username || ''}`.trim();

        setEditForm(prev => ({
            ...prev,
            owner_id: user.id,
            owner_label: label
        }));

        setOwnerSearch(label);
        setIsOwnerDropdownOpen(false);
        setError('');
    };

    const clearOwner = () => {
        setEditForm(prev => ({
            ...prev,
            owner_id: '',
            owner_label: ''
        }));

        setOwnerSearch('');
        setOwnerUsers([]);
        setIsOwnerDropdownOpen(true);

        setTimeout(() => {
            searchOwners('');
        }, 0);
    };

    const clearSearch = () => {
        setSearch('');
    };

    return (
        <div className={styles.container}>
            <CircuitBackgroundLayer />
            <div className={styles.overlay}></div>

            <div className={styles.content}>
                <div className={styles.header}>
                    <div className={styles.deviceTitle}>
                        <span style={{ fontSize: '2rem' }}>🔌</span>
                        <div>
                            <div style={{ fontWeight: 'bold' }}>مدیریت دستگاه‌ها</div>
                            <span style={{ fontSize: '12px', color: '#ccc' }}>
                                مشاهده و ویرایش دستگاه‌ها، مالک و تاریخ سرویس
                            </span>
                        </div>
                    </div>

                    <button
                        onClick={() => navigate('/admin')}
                        className={styles.backBtn}
                        type="button"
                    >
                        <FaArrowLeft /> بازگشت به مدیریت
                    </button>
                </div>

                <div style={topBarStyle}>
                    <div style={searchWrapperStyle}>
                        <FaSearch style={searchIconInlineStyle} />

                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="جستجو با سریال، نام دستگاه، مالک یا موبایل..."
                            style={searchInputStyle}
                        />

                        {search && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                style={clearSearchButtonStyle}
                                title="پاک کردن"
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={() => loadDevices(search)}
                        style={refreshButtonStyle}
                        disabled={loading}
                    >
                        <FaSyncAlt />
                        {loading ? 'در حال دریافت...' : 'بروزرسانی'}
                    </button>
                </div>

                {error && (
                    <div style={errorStyle}>
                        {error}
                    </div>
                )}

                <div style={listCardStyle}>
                    <div style={listHeaderStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaMicrochip color="#f1c40f" />
                            <strong>لیست دستگاه‌ها</strong>
                        </div>

                        <span style={{ color: '#bdc3c7', fontSize: '12px' }}>
                            {loading ? 'در حال بارگذاری...' : `${devices.length} دستگاه`}
                        </span>
                    </div>

                    <div style={tableWrapperStyle}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>دستگاه</th>
                                    <th style={thStyle}>سریال</th>
                                    <th style={thStyle}>مالک</th>
                                    <th style={thStyle}>موبایل مالک</th>
                                    <th style={thStyle}>شروع</th>
                                    <th style={thStyle}>پایان</th>
                                    <th style={thStyle}>عملیات</th>
                                </tr>
                            </thead>

                            <tbody>
                                {devices.length === 0 && !loading ? (
                                    <tr>
                                        <td colSpan="7" style={emptyTableStyle}>
                                            دستگاهی برای نمایش وجود ندارد.
                                        </td>
                                    </tr>
                                ) : (
                                    devices.map(device => (
                                        <tr key={device.id}>
                                            <td style={tdStyle}>
                                                <div style={{ fontWeight: 'bold' }}>
                                                    {device.device_name || 'بدون نام'}
                                                </div>
                                            </td>

                                            <td style={tdStyle}>
                                                <code style={codeStyle}>
                                                    {device.device_serial}
                                                </code>
                                            </td>

                                            <td style={tdStyle}>
                                                <div style={{ fontWeight: 'bold' }}>
                                                    {buildOwnerName(device) || 'بدون نام'}
                                                </div>
                                                <div style={{ color: '#95a5a6', fontSize: '12px', marginTop: '4px' }}>
                                                    {device.owner_username}
                                                </div>
                                            </td>

                                            <td style={tdStyle}>
                                                {device.owner_phone || '-'}
                                            </td>

                                            <td style={tdStyle}>
                                                {formatDate(device.start_date)}
                                            </td>

                                            <td style={tdStyle}>
                                                {formatDate(device.due_date)}
                                            </td>

                                            <td style={tdStyle}>
                                                <button
                                                    type="button"
                                                    onClick={() => openEditModal(device)}
                                                    style={editButtonStyle}
                                                >
                                                    <FaEdit />
                                                    ویرایش
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {editDevice && (
                <div style={modalOverlayStyle}>
                    <form onSubmit={submitEditDevice} style={modalStyle}>
                        <div style={modalHeaderStyle}>
                            <strong>ویرایش دستگاه</strong>

                            <button
                                type="button"
                                onClick={closeEditModal}
                                style={smallCloseButtonStyle}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={modalBodyStyle}>
                            <EditInput
                                label="سریال دستگاه"
                                value={editForm.device_serial}
                                onChange={(v) => setEditForm(prev => ({ ...prev, device_serial: v }))}
                            />

                            <EditInput
                                label="نام دستگاه"
                                value={editForm.device_name}
                                onChange={(v) => setEditForm(prev => ({ ...prev, device_name: v }))}
                            />

                            <div style={ownerDropdownWrapperStyle} ref={ownerDropdownRef}>
                                <label style={modalLabelStyle}>مالک دستگاه</label>

                                <div style={ownerSearchInputWrapperStyle}>
                                    <input
                                        value={ownerSearch}
                                        onFocus={handleOwnerInputFocus}
                                        onClick={handleOwnerInputFocus}
                                        onChange={(e) => {
                                            setOwnerSearch(e.target.value);
                                            setEditForm(prev => ({
                                                ...prev,
                                                owner_id: '',
                                                owner_label: ''
                                            }));
                                            setIsOwnerDropdownOpen(true);
                                        }}
                                        placeholder="جستجوی مالک..."
                                        style={{
                                            ...modalInputStyle,
                                            paddingLeft: editForm.owner_id ? '38px' : '12px'
                                        }}
                                    />

                                    {editForm.owner_id && (
                                        <button
                                            type="button"
                                            style={clearOwnerButtonStyle}
                                            onClick={clearOwner}
                                            title="حذف انتخاب"
                                        >
                                            <FaTimes />
                                        </button>
                                    )}

                                    {!editForm.owner_id && (
                                        <div style={ownerSearchIconStyle}>
                                            {ownerUsersLoading ? '...' : <FaSearch />}
                                        </div>
                                    )}
                                </div>

                                {isOwnerDropdownOpen && (
                                    <div style={ownerDropdownStyle}>
                                        <div style={dropdownHeaderStyle}>
                                            <span>
                                                {ownerUsersLoading
                                                    ? 'در حال دریافت کاربران...'
                                                    : `پیشنهاد کاربران (${ownerUsers.length})`}
                                            </span>

                                            <button
                                                type="button"
                                                style={dropdownCloseButtonStyle}
                                                onClick={() => setIsOwnerDropdownOpen(false)}
                                            >
                                                <FaTimes />
                                            </button>
                                        </div>

                                        <div style={dropdownListStyle}>
                                            {ownerUsers.length === 0 && !ownerUsersLoading ? (
                                                <div style={emptyUsersStyle}>
                                                    کاربری برای نمایش وجود ندارد.
                                                </div>
                                            ) : (
                                                ownerUsers.map(user => (
                                                    <div
                                                        key={user.id}
                                                        style={ownerItemStyle}
                                                        onClick={() => handleSelectOwner(user)}
                                                    >
                                                        <div>
                                                            <strong>
                                                                {(user.name || '')} {(user.family || '')}
                                                            </strong>
                                                            <div style={{ color: '#95a5a6', fontSize: '12px', marginTop: '4px' }}>
                                                                {user.username} | {user.phone_num1}
                                                            </div>
                                                        </div>

                                                        <div style={deviceCountBadgeStyle}>
                                                            {user.devices_count || 0} دستگاه
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <EditInput
                                label="تاریخ شروع"
                                type="date"
                                value={editForm.start_date}
                                onChange={(v) => setEditForm(prev => ({ ...prev, start_date: v }))}
                            />

                            <EditInput
                                label="تاریخ پایان سرویس"
                                type="date"
                                value={editForm.due_date}
                                onChange={(v) => setEditForm(prev => ({ ...prev, due_date: v }))}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={editLoading}
                            style={saveEditButtonStyle}
                        >
                            <FaSave />
                            {editLoading ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

const EditInput = ({ label, value, onChange, type = 'text' }) => (
    <div>
        <label style={modalLabelStyle}>{label}</label>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={modalInputStyle}
        />
    </div>
);

const buildOwnerName = (device) => {
    return `${device.owner_name || ''} ${device.owner_family || ''}`.trim();
};

const buildOwnerLabel = (device) => {
    const name = buildOwnerName(device);
    const phone = device.owner_phone || device.owner_username || '';
    return `${name} - ${phone}`.trim();
};

const formatDate = (date) => {
    if (!date) return '-';

    try {
        return new Date(date).toLocaleDateString('fa-IR');
    } catch {
        return date;
    }
};

const topBarStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: '12px',
    marginTop: '24px',
    direction: 'rtl'
};

const searchWrapperStyle = {
    position: 'relative'
};

const searchInputStyle = {
    width: '100%',
    background: 'rgba(16, 24, 32, 0.92)',
    color: '#fff',
    border: '1px solid rgba(52, 152, 219, 0.35)',
    borderRadius: '12px',
    padding: '12px 40px 12px 42px',
    outline: 'none',
    boxSizing: 'border-box'
};

const searchIconInlineStyle = {
    position: 'absolute',
    right: '13px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#4dabf7'
};

const clearSearchButtonStyle = {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(231,76,60,0.18)',
    color: '#ffb3b3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
};

const refreshButtonStyle = {
    background: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    padding: '0 18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontWeight: 'bold',
    minHeight: '44px'
};

const errorStyle = {
    background: 'rgba(231, 76, 60, 0.15)',
    border: '1px solid rgba(231, 76, 60, 0.4)',
    color: '#ffb3b3',
    borderRadius: '10px',
    padding: '11px',
    marginTop: '14px',
    fontSize: '13px',
    direction: 'rtl'
};

const listCardStyle = {
    background: 'rgba(31, 45, 58, 0.92)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px',
    padding: '16px',
    color: '#fff',
    marginTop: '18px',
    boxShadow: '0 10px 35px rgba(0,0,0,0.25)',
    direction: 'rtl'
};

const listHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '14px'
};

const tableWrapperStyle = {
    overflowX: 'auto'
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '980px',
    fontSize: '13px'
};

const thStyle = {
    textAlign: 'right',
    color: '#f1c40f',
    background: '#101820',
    padding: '12px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    position: 'sticky',
    top: 0
};

const tdStyle = {
    padding: '11px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    color: '#ecf0f1',
    verticalAlign: 'middle'
};

const emptyTableStyle = {
    padding: '30px',
    textAlign: 'center',
    color: '#95a5a6'
};

const codeStyle = {
    background: '#101820',
    color: '#f1c40f',
    borderRadius: '6px',
    padding: '4px 7px',
    direction: 'ltr',
    display: 'inline-block'
};

const editButtonStyle = {
    background: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
};

const modalOverlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.65)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
};

const modalStyle = {
    width: 'min(760px, 95vw)',
    background: '#1f2d3a',
    color: '#fff',
    borderRadius: '16px',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
    padding: '0',
    direction: 'rtl',
    overflow: 'visible'
};

const modalHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#17212b',
    padding: '14px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    color: '#f1c40f',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px'
};

const modalBodyStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))',
    gap: '14px',
    padding: '16px'
};

const modalLabelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    color: '#bdc3c7'
};

const modalInputStyle = {
    width: '100%',
    background: '#101820',
    color: '#fff',
    border: '1px solid #34495e',
    borderRadius: '8px',
    padding: '10px',
    outline: 'none',
    boxSizing: 'border-box'
};

const smallCloseButtonStyle = {
    border: 'none',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const saveEditButtonStyle = {
    margin: '0 16px 16px',
    width: 'calc(100% - 32px)',
    background: '#2ecc71',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontWeight: 'bold'
};

const ownerDropdownWrapperStyle = {
    position: 'relative'
};

const ownerSearchInputWrapperStyle = {
    position: 'relative'
};

const clearOwnerButtonStyle = {
    position: 'absolute',
    left: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(231, 76, 60, 0.18)',
    color: '#ffb3b3',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const ownerSearchIconStyle = {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#95a5a6',
    fontSize: '13px'
};

const ownerDropdownStyle = {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    left: 0,
    zIndex: 100000,
    background: '#101820',
    border: '1px solid rgba(52, 152, 219, 0.35)',
    borderRadius: '12px',
    boxShadow: '0 14px 40px rgba(0,0,0,0.45)',
    overflow: 'hidden'
};

const dropdownHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '9px 11px',
    background: '#17212b',
    color: '#bdc3c7',
    fontSize: '12px',
    borderBottom: '1px solid rgba(255,255,255,0.08)'
};

const dropdownCloseButtonStyle = {
    width: '24px',
    height: '24px',
    border: 'none',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const dropdownListStyle = {
    maxHeight: '260px',
    overflowY: 'auto',
    padding: '8px',
    display: 'grid',
    gap: '8px'
};

const ownerItemStyle = {
    background: '#17212b',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px',
    padding: '11px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px'
};

const emptyUsersStyle = {
    background: '#17212b',
    borderRadius: '10px',
    padding: '16px',
    color: '#95a5a6',
    fontSize: '13px',
    border: '1px dashed rgba(255,255,255,0.12)'
};

const deviceCountBadgeStyle = {
    background: 'rgba(52,152,219,0.14)',
    color: '#4dabf7',
    border: '1px solid rgba(52,152,219,0.28)',
    borderRadius: '999px',
    padding: '4px 9px',
    fontSize: '11px',
    whiteSpace: 'nowrap'
};

export default AdminDevices;